import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronDown, Copy, Download, ExternalLink, Moon, Sun } from "lucide-react";
import { ICON_CONFIG } from "../config";
import { toolExamples } from "../data/updates";
import { basicPayload, chatPayload, codexConfig, curlRequest, desktopConfig, docErrorRows, docsNavigation, sdkExample, streamExample } from "../data/developerDocs";
import "./DocsPage.css";

const activeSection = () => docsNavigation.some(item => item.id === window.location.hash.slice(1)) ? window.location.hash.slice(1) : "quickstart";

export function CodeBlock({ label, language = "JSON", children }) {
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!status) return undefined;
    const timer = window.setTimeout(() => setStatus(""), 2500);
    return () => window.clearTimeout(timer);
  }, [status]);
  return <div className="docs-code">
    <div className="docs-code-toolbar"><span>{label}</span><span className="docs-code-actions"><small>{language}</small>
      <button type="button" aria-label={`复制${label}`} title={`复制${label}`} onClick={async () => {
        try { await navigator.clipboard.writeText(children); setStatus("已复制"); }
        catch { setStatus("复制失败，请选取代码复制"); }
      }}>{status === "已复制" ? <Check size={15} /> : <Copy size={15} />}</button>
    </span></div>
    <pre tabIndex={0} aria-label={label}><code>{children}</code></pre>
    {status && <span className="docs-copy-status" role="status">{status}</span>}
  </div>;
}

function Table({ headings, rows, label }) {
  return <div className="docs-table-scroll" tabIndex={0} role="region" aria-label={label}>
    <table><thead><tr>{headings.map(h => <th scope="col" key={h}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody></table>
  </div>;
}

function Quickstart() {
  return <>
    <div className="docs-endpoint"><span>BASE URL</span><code>https://api.lumanova.icu/v1</code></div>
    <h2>1. 创建 API Key</h2>
    <p>登录 LumaNova，在左下角账号菜单打开「密钥管理」，创建并妥善保存自己的密钥。网页登录状态不用于 API 鉴权，也不要填写上游厂商的 Key。</p>
    <CodeBlock label="设置环境变量" language="Shell">{'export LUMA_API_KEY="<你的 LumaNova API Key>"'}</CodeBlock>
    <p className="docs-caption">以上为 macOS / Linux 写法。PowerShell 使用 <code>$env:LUMA_API_KEY = "&lt;你的 LumaNova API Key&gt;"</code>。</p>
    <h2>2. 确认可用模型</h2>
    <CodeBlock label="查询模型" language="cURL">{'curl "https://api.lumanova.icu/v1/models" -H "Authorization: Bearer $LUMA_API_KEY"'}</CodeBlock>
    <p>从 <code>data[].id</code> 读取模型名称。本文使用 <code>deepseek-flash</code>；可用模型以接口实际返回为准，模型列表不代表每个模型都支持图片或工具。</p>
    <h2>3. 发起第一次请求</h2>
    <CodeBlock label="文本请求" language="cURL">{curlRequest(basicPayload)}</CodeBlock>
    <p>从 <code>output</code> 中找到 <code>type: "message"</code> 的输出项，再读取 <code>content</code> 中 <code>type: "output_text"</code> 的文本。使用 Python SDK 时，可以直接读取 <code>response.output_text</code>。</p>
    <h2>使用 OpenAI Python SDK</h2>
    <p>安装 <code>openai</code> 包后配置自定义 Base URL。示例从环境变量读取 Key，不把密钥写进源码。</p>
    <CodeBlock label="Python 最小示例" language="Python">{sdkExample}</CodeBlock>
    <div className="docs-note">示例会真实消耗额度。本文不提供在线调用调试器；密钥仅保存在你自己的运行环境中。</div>
  </>;
}

function Responses() {
  return <>
    <div className="docs-endpoint"><span>POST</span><code>/v1/responses</code></div>
    <p>推荐新集成使用 Responses。普通模型请求和客户端工具请求走模型转发；声明 Luma 内置工具时，由 Luma 负责理解需求、执行工具并整理结果。</p>
    <Table label="常用请求参数" headings={["参数", "用途"]} rows={[
      ["model", "必填，语言模型名称。图片任务也填写语言模型，而不是图片权重名。"],
      ["input", "字符串，或包含 role / content 的消息数组；多轮时携带必要历史。"],
      ["instructions", "本次请求的系统指令。"],
      ["tools / tool_choice", "可用工具及选择方式；auto 由模型决定，指定类型则要求使用对应工具。"],
      ["stream", "false 返回完整 JSON；true 返回 Responses SSE 事件。"],
      ["store", "示例统一使用 false。Luma 托管工具不提供跨请求会话保存。"],
      ["max_output_tokens", "限制生成 Token；托管工具模式支持 1–8192。"],
    ]} />
    <CodeBlock label="Responses 请求体">{JSON.stringify(basicPayload, null, 2)}</CodeBlock>
    <h2>结果不止是一段文字</h2>
    <p><code>output</code> 可包含消息、工具记录或图片。托管工具调用需检查顶层 <code>status</code> 和每个目标工具项的状态；HTTP 200 不一定代表任务成功。</p>
    <Table label="返回字段" headings={["字段", "读取方式"]} rows={[
      ["status / error", "completed 表示完成；failed 时查看错误信息。转发模式以实际模型返回为准。"],
      ["output[]", "message 为文本；web_search_call 为搜索记录；image_generation_call 为图片结果。"],
      ["usage", "各轮模型报告的输入、输出及总 Token。"],
      ["billing", "Luma 托管扩展：实际结算的 Token 和图片次数，排错时以此为准。"],
      ["request_id", "Luma 托管任务标识，用于排查；不是可续接对话的会话 ID。"],
    ]} />
    <h2>流式接收</h2>
    <CodeBlock label="SSE 接收示例" language="Python">{streamExample}</CodeBlock>
    <p>处理 <code>response.output_text.delta</code> 获取文本，最终检查 <code>response.completed</code> 或 <code>response.failed</code>。等待托管工具时可能收到 keep-alive 注释；这不是错误，也不是 Chat 协议的 <code>[DONE]</code>。</p>
    <p>托管工具的文本会在执行与整理后分段发送，不等于上游逐 Token 透传。长任务请给客户端和反向代理留足等待时间，示例客户端等待 3600 秒。</p>
    <h2>客户端工具与托管工具</h2>
    <p>Codex 使用的终端、文件修改等工具由 Codex 客户端执行，不是 Luma 云端替你操作电脑。纯 <code>function</code> / <code>custom</code> 工具请求走上游兼容转发；支持范围由上游和客户端决定。</p>
    <div className="docs-note">当前不能在同一请求中混用客户端 function / custom / MCP 工具与 Luma 托管的 web_search、image_generation。Codex 接入指南默认关闭内置联网搜索，不能据此推断 Codex 的所有功能均已兼容。</div>
    <h2>兼容 Chat Completions</h2>
    <p>已有文本应用仍可调用 <code>POST /v1/chat/completions</code>，使用 <code>messages</code> 传入消息，从 <code>choices[0].message.content</code> 读取非流式文本。Luma 内置工具请使用 Responses。</p>
    <CodeBlock label="Chat Completions" language="cURL">{curlRequest(chatPayload, "chat/completions")}</CodeBlock>
  </>;
}

function Tools() {
  const [selected, setSelected] = useState("search");
  const example = toolExamples.find(item => item.id === selected);
  return <>
    <p>不需要自己编排模型、搜索和图片服务。在 <code>tools</code> 中声明能力，Luma 会在本次请求内执行。以下三个示例使用同一个 Responses 接口。</p>
    <div className="docs-tool-tabs" role="tablist" aria-label="工具示例">{toolExamples.map(item =>
      <button key={item.id} id={`tool-tab-${item.id}`} role="tab" type="button" tabIndex={selected === item.id ? 0 : -1} aria-selected={selected === item.id} aria-controls="docs-tool-panel" onClick={() => setSelected(item.id)} onKeyDown={event => {
        const index = toolExamples.findIndex(option => option.id === selected);
        const next = event.key === "ArrowRight" ? (index + 1) % toolExamples.length : event.key === "ArrowLeft" ? (index + toolExamples.length - 1) % toolExamples.length : event.key === "Home" ? 0 : event.key === "End" ? toolExamples.length - 1 : null;
        if (next !== null) { event.preventDefault(); setSelected(toolExamples[next].id); event.currentTarget.parentElement.querySelectorAll('button')[next].focus(); }
      }}>{item.name}</button>
    )}</div>
    <section id="docs-tool-panel" role="tabpanel" aria-labelledby={`tool-tab-${selected}`}>
      <h2>{example.name}</h2><p>{example.description}</p>
      <CodeBlock key={example.id} label={`${example.name}请求体`}>{JSON.stringify(example.payload, null, 2)}</CodeBlock>
      <p>{example.result}</p>
    </section>
    <h2>如何发送</h2>
    <p>将上面的 JSON 作为请求体发送到 <code>POST https://api.lumanova.icu/v1/responses</code>，或传给 SDK 的 <code>client.responses.create(**payload)</code>。</p>
    <CodeBlock label="托管工具请求头" language="HTTP">{'Authorization: Bearer <你的 LumaNova API Key>\nContent-Type: application/json\nIdempotency-Key: <本次请求的唯一 UUID>'}</CodeBlock>
    <p>每个新任务使用新的 UUID。遇到网络结果不确定时，保留原键和完全相同的参数；不要换新键自动重试。幂等键用于避免重复执行，不用于重新下载旧结果。</p>
    <h2>读取图片结果</h2>
    <CodeBlock label="提取图片" language="Python">{`import base64
from pathlib import Path

body = response.model_dump()
if body.get("status") != "completed":
    raise RuntimeError(body.get("error"))
for item in body.get("output", []):
    if item.get("type") == "image_generation_call" and item.get("status") == "completed":
        if item.get("result"):
            Path("result.image").write_bytes(base64.b64decode(item["result"], validate=True))
        elif item.get("image_url"):
            print(item["image_url"])`}</CodeBlock>
    <h2>上下文由你管理</h2>
    <p>接口不替调用方保存跨请求对话。说「把上一张改成水彩」时，须把上一张的可访问图片 URL 和修改要求一起放入本轮 <code>input</code>，或携带含 URL 的必要历史。</p>
    <p>不支持用 <code>previous_response_id</code> 或 <code>conversation</code> 自动取历史。无状态不等于完全不落盘：图片会进入 Luma 媒体缓存/CDN，任务与结算也有必要记录。</p>
    <h2>当前边界</h2>
    <ul><li>每请求最多执行 4 次工具，最多生成或编辑 1 张图；参考图须为实际可下载的公开 HTTP(S) URL。</li>
      <li>图片编辑暂不接受内网 URL、file_id、data URL 或 mask。示例 example.com 地址需替换。</li>
      <li>不支持 background、自动历史截断或 JSON Schema 强制输出。可用参数不等于官方 Responses 的全部能力。</li></ul>
  </>;
}

function Codex() {
  return <>
    <div className="docs-note">这是 LumaNova 的实测第三方接入方案，不是 OpenAI 官方模型接入承诺。先备份，再合并配置；不需要修改项目文件，也不要直接覆盖整份 config.toml。</div>
    <h2>1. 备份当前配置</h2>
    <p>在用户目录打开 <code>~/.codex/</code>。如果设置了自定义 CODEX_HOME，请使用实际目录。先保留原始 <code>config.toml</code>，以后恢复官方环境时使用。</p>
    <CodeBlock label="备份 config.toml" language="Shell">{'mkdir -p ~/.codex\nif [ -f ~/.codex/config.toml ]; then\n  cp -p ~/.codex/config.toml ~/.codex/config.toml.backup-$(date +%Y%m%d-%H%M%S)\nfi'}</CodeBlock>
    <details className="docs-details"><summary>Windows PowerShell</summary>
      <CodeBlock label="Windows 备份" language="PowerShell">{'New-Item -ItemType Directory -Force "$env:USERPROFILE\\.codex" | Out-Null\nif (Test-Path "$env:USERPROFILE\\.codex\\config.toml") {\n  Copy-Item "$env:USERPROFILE\\.codex\\config.toml" "$env:USERPROFILE\\.codex\\config.toml.backup-$(Get-Date -Format yyyyMMdd-HHmmss)"\n}'}</CodeBlock>
    </details>
    <h2>2. 准备 models.json</h2>
    <p>下载模型目录文件，放到 <code>~/.codex/models.json</code>。如果已有自定义 models.json，先备份再替换或合并 <code>models</code> 数组，避免覆盖其他配置。</p>
    <a className="docs-download" href="/developer-assets/models.json" download="models.json"><Download size={17} />下载 models.json<span>JSON · 实测配置</span></a>
    <p>完整模型目录包含 <code>deepseek-flash</code> 与 <code>deepseek-v4-pro</code>。接入实测以 deepseek-flash 为准；目录声明客户端最低版本为 <code>0.144.0</code>。</p>
    <p className="docs-caption">目录中的上下文长度、图像能力与推理档位是配置声明，不会改变真实模型能力。目录还包含自定义 Agent 指令模板，导入后会影响模型行为；可按需审阅调整。不同版本客户端的目录格式可能变化。</p>
    <h2>3. 合并 config.toml</h2>
    <p>用文本编辑器打开 <code>~/.codex/config.toml</code>，更新下列配置。根级字段必须放在任何 <code>[table]</code> 之前；同名字段和同名表修改原有项，不要重复追加。</p>
    <CodeBlock label="LumaNova Codex 配置" language="TOML">{codexConfig}</CodeBlock>
    <p>把占位符换成你自己的 LumaNova Key。Base URL 使用纯地址，不要包含 Markdown 的方括号。路径里的 <code>~</code> 不要写成 <code>\~</code>；如果客户端无法展开，请改为你电脑的绝对路径。</p>
    <p>Windows 可使用 <code>model_catalog_json = 'C:\Users\你的用户名\.codex\models.json'</code>。TOML 单引号可以避免反斜杠转义问题。</p>
    <details className="docs-details"><summary>可选：桌面端推理档位显示</summary>
      <CodeBlock label="桌面档位设置" language="TOML">{desktopConfig}</CodeBlock>
      <p>这是实测环境中的桌面端设置，不是所有版本的通用要求。显示更多档位不意味着上游支持所有档位；以模型目录和接口实际支持为准。已有 [desktop] 表时只合并字段。</p>
    </details>
    <details className="docs-details"><summary>可选：使用环境变量保存 Key</summary>
      <p>上面的直填方式便于快速接入，但密钥会明文保存在本机。更适合共享配置的做法是删除 <code>experimental_bearer_token</code>，改用下列配置，并确保启动 Codex 的进程能读到环境变量。</p>
      <CodeBlock label="环境变量鉴权" language="TOML">{'# 放在 [model_providers.lumanova] 表内，二选一，不要与直填 Token 并用。\nenv_key = "LUMA_API_KEY"'}</CodeBlock>
      <p>从 Dock 启动的 macOS 应用不一定继承终端 export；请确认实际启动环境。不要把真实 config.toml 发到群聊或提交到仓库。</p>
    </details>
    <h2>4. 保存并完全重启 Codex</h2>
    <p>退出并重新启动 Codex，选择或确认 <code>deepseek-flash</code>，新建对话测试。保存配置后只关闭聊天标签不一定会重新加载模型目录。</p>
    <p>用量可到 LumaNova 网页的「账号菜单 → 剩余用量 / 用量与订阅」查看，以后台结算后的实际数值为准。</p>
    <h2>几个容易误解的设置</h2>
    <ul><li><code>web_search = "disabled"</code> 关闭此客户端配置中的联网搜索，不会关闭 Luma API 独立请求的搜索能力。</li>
      <li><code>show_raw_agent_reasoning</code> 只影响模型实际返回相关内容时的显示，不会强迫模型输出推理，也不会增加模型能力。</li>
      <li><code>notify</code> 通知命令与本机安装路径有关，公共示例未包含；保留自己已有且有效的通知配置即可。</li>
      <li>终端、文件编辑、MCP 等是否可用取决于客户端和上游支持。不要把“可连接”理解为所有 Codex 功能均兼容。</li></ul>
    <h2>恢复官方环境</h2>
    <p>退出 Codex，用第一步保留的备份恢复 <code>config.toml</code>，再重新启动。无需删除聊天记录或卸载应用；恢复后不再引用自定义 models.json 时，该文件可以保留。</p>
    <p className="docs-sources">配置字段参考 <a href="https://learn.chatgpt.com/docs/config-file/config-reference" target="_blank" rel="noreferrer">OpenAI 配置文档<ExternalLink size={12} /></a>。LumaNova 接入结果以当前版本实测为准。</p>
  </>;
}

function Usage() {
  return <>
    <h2>在哪里查看</h2><p>登录 LumaNova 网页，在左下角账号菜单展开「剩余用量」快速查看，或进入「用量与订阅」查看完整额度与重置时间。</p>
    <p>网页与 API 共用连续 7 天的 Token 总额度，各自有独立的 5 小时窗口。首次实际消耗后开始计时，不按每周一重置。图片生成、编辑与网页 Chatbot 共用按日重置的图片额度。</p>
    <h2>托管工具如何计数</h2>
    <Table label="托管工具计费规则" headings={["结果", "Token", "图片次数"]} rows={[
      ["搜索成功", "累计实际模型用量；搜索工具不额外收费", "不扣"],
      ["生成 / 编辑成功", "累计实际模型用量", "按成功张数扣次"],
      ["明确的安全拦截，解释正常返回", "已产生的模型 Token 正常计数", "不扣"],
      ["接口故障 / 超时等技术失败", "本次托管免扣；以 billing 为准", "不扣，释放暂占"],
      ["billing_pending", "状态未确认，需要核查", "状态未确认，需要核查"],
    ]} />
    <p><code>usage</code> 反映模型报告的计算用量，<code>billing</code> 反映托管任务实际结算。普通模型转发按自身接口规则计数，不要套用托管失败免扣规则。</p>
    <h2>常见问题</h2><Table label="状态与排错" headings={["状态", "含义", "建议处理"]} rows={docErrorRows} />
    <h2>重试前先保留证据</h2>
    <p>记录请求时间、模型、HTTP 状态、<code>request_id</code>、幂等键、<code>error</code>、<code>usage</code> 与 <code>billing</code>。反馈时隐去 Authorization，不要公开密钥或大段图片 Base64。</p>
    <p>生成图片可能需要较长时间。客户端超时不会保证服务器任务停止；不要因为断线就创建一个新的任务。重复请求返回 409 时，也不代表原任务失败或没有扣费。</p>
  </>;
}

const content = { quickstart: Quickstart, responses: Responses, tools: Tools, codex: Codex, usage: Usage };

export default function DocsPage({ isDark, onThemeToggle, onBack, onUpdates }) {
  const [active, setActive] = useState(activeSection);
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollRef = useRef(null);
  const headingRef = useRef(null);
  useEffect(() => {
    const sync = () => { setActive(activeSection()); setMenuOpen(false); };
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  const select = (id) => {
    setActive(id); setMenuOpen(false);
    window.history.replaceState(window.history.state, "", `/docs#${id}`);
    scrollRef.current?.scrollTo?.({ top: 0 });
    headingRef.current?.focus({ preventScroll: true });
  };
  const current = docsNavigation.find(item => item.id === active);
  const Content = content[active];
  const next = docsNavigation[docsNavigation.findIndex(item => item.id === active) + 1];
  return <div className={`docs-page ${isDark ? "theme-dark" : "theme-light"}`}>
    <header className="docs-header"><a className="docs-brand" href="/" onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onBack(); } }}><img src={ICON_CONFIG.logo} alt="" width="27" height="27" /><strong>LumaNova</strong><span>开发文档</span></a>
      <nav aria-label="文档页面导航"><button type="button" onClick={onUpdates}>更新公告</button><button type="button" onClick={onBack}><ArrowLeft size={15} /><span>返回对话</span></button>
        <button className="docs-icon" type="button" onClick={onThemeToggle} aria-label={isDark ? "切换浅色模式" : "切换深色模式"} title={isDark ? "切换浅色模式" : "切换深色模式"}>{isDark ? <Sun size={17} /> : <Moon size={17} />}</button></nav>
    </header>
    <div className="docs-layout">
      <aside className={`docs-sidebar ${menuOpen ? "is-open" : ""}`}><button type="button" className="docs-mobile-menu" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-controls="docs-chapters"><BookOpen size={16} />{current.title}<ChevronDown size={16} /></button>
        <nav id="docs-chapters" aria-label="文档章节">{docsNavigation.map((item, index) => <React.Fragment key={item.id}>
          {docsNavigation[index - 1]?.group !== item.group && <p>{item.group}</p>}
          <a href={`/docs#${item.id}`} aria-current={active === item.id ? "page" : undefined} onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); select(item.id); } }}>{item.title}</a>
        </React.Fragment>)}<a className="docs-markdown" href="/developer-assets/lumanova-api.md" download><Download size={14} />下载 Markdown</a></nav>
      </aside>
      <main className="docs-reader" ref={scrollRef}><article className="docs-article">
        <div className="docs-breadcrumb">开发文档<span>/</span>{current.group}</div>
        <h1 ref={headingRef} tabIndex={-1}>{current.title}</h1><p className="docs-lead">{current.summary}</p>
        <div className="docs-body"><Content key={active} /></div>
        {next && <a className="docs-next" href={`/docs#${next.id}`} onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); select(next.id); } }}><span><small>继续阅读</small>{next.title}</span><ArrowRight size={18} /></a>}
        <footer className="docs-footer"><span>LumaNova Developers</span><span>更新于 2026.10.28</span></footer>
      </article></main>
    </div>
  </div>;
}
